import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { DashboardCard } from "@/components/dashboard/dashboard-card";
import { getTikTokHourlyPerformance, type TikTokHourlyDay } from "@/services/tiktokShop.service";
import { formatAmount } from "@/utils/formatters";

/**
 * Magnitude as depth of the brand chart color: one sequential ramp built by
 * mixing it toward transparent, so the heatmap recolors with the white-label
 * palette like every other single-channel chart.
 */
const cellColor = (strength: number) =>
  `color-mix(in srgb, var(--chart-1) ${Math.round(strength * 100)}%, transparent)`;

function weekday(date: string) {
  const d = new Date(date);
  return Number.isNaN(d.getTime()) ? date : d.toLocaleDateString("en-US", { weekday: "short" });
}

/**
 * When TikTok sells: GMV per hour over the last seven days as a single-hue
 * grid (magnitude = teal depth, one sequential ramp). The point is schedule
 * advice - the dark columns are when LIVEs and posts should land.
 */
export function TikTokHeatmapCard() {
  const query = useQuery({
    queryKey: ["tiktok-hourly-performance"],
    queryFn: getTikTokHourlyPerformance,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  const { days, max } = useMemo(() => {
    const rows: TikTokHourlyDay[] = query.data?.days ?? [];
    let top = 0;
    for (const day of rows) for (const h of day.hours ?? []) top = Math.max(top, h.gmv);
    return { days: rows, max: top };
  }, [query.data]);

  return (
    <DashboardCard id="dashboard-tiktok-heatmap-card" title="When TikTok sells" bodyClassName="px-5 pb-4">
      {query.isError ? (
        <p className="text-sm text-muted-foreground">
          TikTok hourly data could not be loaded. Try again in a few minutes.
        </p>
      ) : query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading the last seven days…</p>
      ) : max === 0 ? (
        <p className="text-sm text-muted-foreground">No TikTok Shop sales in the last seven days.</p>
      ) : (
        <>
          <p className="pb-3 text-sm text-muted-foreground">
            GMV by hour over the last seven days. The darkest cells are when this shop sells most — schedule LIVEs
            and posts into those windows.
          </p>
          <div className="grid gap-0.5" style={{ gridTemplateColumns: "2.5rem repeat(24, minmax(0, 1fr))" }}>
            {days.map((day) => (
              <div key={day.date} className="contents">
                <span className="pr-2 text-right text-[11px] leading-4 text-muted-foreground">
                  {weekday(day.date)}
                </span>
                {Array.from({ length: 24 }, (_, hour) => {
                  const cell = day.hours?.find((h) => h.hour === hour);
                  const gmv = cell?.gmv ?? 0;
                  const alpha = day.hours === null ? 0 : gmv > 0 ? 0.15 + 0.85 * (gmv / max) : 0.04;
                  return (
                    <span
                      key={hour}
                      role="img"
                      aria-label={`${day.date} ${hour}:00 — ${day.hours === null ? "no data" : formatAmount(gmv)}`}
                      title={`${day.date} ${hour}:00–${hour + 1}:00 · ${
                        day.hours === null ? "no data yet" : `${formatAmount(gmv)} GMV`
                      }`}
                      className="h-4 rounded-[3px]"
                      style={{
                        backgroundColor:
                          day.hours === null ? "hsl(var(--muted))" : cellColor(alpha),
                      }}
                    />
                  );
                })}
              </div>
            ))}
            <span />
            {Array.from({ length: 24 }, (_, hour) => (
              <span key={hour} className="pt-1 text-center text-[10px] leading-3 text-muted-foreground">
                {hour % 6 === 0 ? `${hour}h` : ""}
              </span>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
            Less
            {[0.08, 0.3, 0.55, 0.8, 1].map((alpha) => (
              <span key={alpha} className="h-3 w-5 rounded-[3px]" style={{ backgroundColor: cellColor(alpha) }} />
            ))}
            More · hours in the shop's timezone
          </div>
        </>
      )}
    </DashboardCard>
  );
}
